import { getDefaultBuConfig } from "domain/repository/apiUtil";
import { consolidationMethodsOict, consolidationMethodsSapt } from "presentation/constant/RadioOptions/StaticRadioOptions";
import { RecalProcessAndGenConstant } from "presentation/constant/Recalculation/RecalProcessAndGenConstant";
import { useRecalProcessAndGenVM } from "presentation/hook/Recalculation/useRecalProcessAndGenVM";
import { useMessageBarVM } from "presentation/hook/useMessageBar";
import { useANAInfoTracked } from "presentation/store/ANAInfo";
import { useRecalProcessAndGenMaintenanceTracked } from "presentation/store/Recalculation/RecalProcessAndGenMaintenanceProvider";
import { CriteriaItemContainer } from "presentation/view/components/CriteriaItemContainer";
import DateRangePickerComponent from "presentation/view/components/DateRangePickerComponent";
import { NbisCommonField } from "presentation/view/components/NbisCommonField";
import NumberInputComponent from "presentation/view/components/NumberInputComponent";
import { useCallback, useMemo, useState } from "react";
import { FieldType, HPHButton, HPHGroupRadioButton, IconButton, IFieldValue, Loader } from "veronica-ui-component/dist/component/core";
import { SidebarActionBar, SidebarActionCross, Sidebarheader, SidebarTitle } from "veronica-ui-component/dist/component/core/styled/uiFramework.styled";

const RecalProcessAndGenGeneratePanel = () => {
    const SCREEN_CONSTANT = RecalProcessAndGenConstant;
    const [recalProcessAndGenState] = useRecalProcessAndGenMaintenanceTracked();
    const recalProcessAndGenVM = useRecalProcessAndGenVM();
    const { generateCriteria, searchCriteria } = recalProcessAndGenState;
    const [isLoading, setIsLoading] = useState(false);
    const messageBarVM = useMessageBarVM();
    const [anainfoState] = useANAInfoTracked();
    const { operatingUnit } = anainfoState;
    const consolidationMethods = (operatingUnit === "AQCT" || operatingUnit === "OICT" || operatingUnit === "HPJZ" || operatingUnit === "RSCT") ? consolidationMethodsOict : consolidationMethodsSapt;
    const [anaInfoState] = useANAInfoTracked();
    const countryCode: string = getDefaultBuConfig<string>("REACT_APP_DEFAULT_COUNTRY") || "PAK";
    const companyCode = anaInfoState.defaultOperatingCompany ?? "";//= "2817";

    const handleGenerate = useCallback(async () => {
        setIsLoading(true);
        await recalProcessAndGenVM.onGenerate(generateCriteria, generateCriteria.consolidationMethod === "SEQ" ? countryCode : null, generateCriteria.consolidationMethod === "SEQ" ? companyCode : null).then(async data => {
            if (data && data.success) {
                await recalProcessAndGenVM.onSearch(searchCriteria).then(data => {
                    setIsLoading(false);
                    messageBarVM.showSuccess("Generate Successfully.");
                })
            } else {
                setIsLoading(false);
                messageBarVM.showError(data.data ?? "Generate Failed.");
                return;
            }
        });
    }, [companyCode, countryCode, generateCriteria, messageBarVM, recalProcessAndGenVM, searchCriteria]);

    const handClose = useCallback(() => {
        recalProcessAndGenVM.onCloseGeneratePanel();
    }, [recalProcessAndGenVM]);

    const resetButtonClicked = useCallback(() => {
        recalProcessAndGenVM.onGenCriteriaResetClick();
    }, [recalProcessAndGenVM]);

    const onNumberFieldChange = useCallback((e: any, fieldName: string, fieldValue: any) => {
        recalProcessAndGenVM.onGenerateFieldChange(fieldName, fieldValue);
    }, [recalProcessAndGenVM]);

    const memoConsolidationMethodGroup = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "470px", marginBottom: "24px" }}>
            <HPHGroupRadioButton
                label="Consolidation Method"
                disabled={false}
                orientation={"vertical"}
                radioOptions={consolidationMethods}
                value={generateCriteria.consolidationMethod ?? ''}
                onChange={(e) => recalProcessAndGenVM.onGenerateRadioChange(e, 'consolidationMethod')}
                checked={consolidationMethods?.find(item => item.key === generateCriteria.consolidationMethod)?.name}
            />
        </div>
        , [consolidationMethods, generateCriteria.consolidationMethod, recalProcessAndGenVM])

    const memoSeqNoFrom = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px" }}>
            <NumberInputComponent
                label={SCREEN_CONSTANT.SEQ_NO_FROM}
                maxLength={10}
                fieldName={"seqNoFrom"}
                value={generateCriteria?.seqNoFrom ?? ''}
                errorMessage={''}
                disabled={false}
                decimalPlaces={0}
                optional={true}
                onChange={(e: any, fieldKey: string, fieldValue: any) => onNumberFieldChange(e, fieldKey, fieldValue)}
            />
        </div>
        , [SCREEN_CONSTANT.SEQ_NO_FROM, generateCriteria?.seqNoFrom, onNumberFieldChange])

    const memoSeqNoTo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px" }}>
            <NumberInputComponent
                label={SCREEN_CONSTANT.SEQ_NO_TO}
                maxLength={10}
                fieldName={"seqNoTo"}
                value={generateCriteria?.seqNoTo ?? ''}
                errorMessage={''}
                disabled={false}
                decimalPlaces={0}
                optional={true}
                onChange={(e: any, fieldKey: string, fieldValue: any) => onNumberFieldChange(e, fieldKey, fieldValue)}
            />
        </div>
        , [SCREEN_CONSTANT.SEQ_NO_TO, generateCriteria?.seqNoTo, onNumberFieldChange])

    const memoBillToCompany = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={generateCriteria?.billToCompanyList?.join(',') ?? ''}
                fieldValue={generateCriteria?.billToCompanyList ?? ''}
                fieldLabel={SCREEN_CONSTANT.BILLTO_COMPANY}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'billToCompanyList'}
                maxLength={60}
                isMultipleDropDown={true}
                isLimitOptions={true}
                optional={true}
                options={recalProcessAndGenState.dynamicOptions.companyCodeDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    recalProcessAndGenVM.onGenerateFieldChange(fieldKey, fieldValue && fieldValue, fFullValue)} />
        </div>
        , [generateCriteria?.billToCompanyList, SCREEN_CONSTANT.BILLTO_COMPANY, recalProcessAndGenState.dynamicOptions.companyCodeDropdownOptions, recalProcessAndGenVM])

    const memoCustomerCode = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={generateCriteria?.customerCodeList?.join(',') ?? ''}
                fieldValue={generateCriteria?.customerCodeList ?? ''}
                fieldLabel={SCREEN_CONSTANT.CUSTOMER_CODE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'customerCodeList'}
                maxLength={60}
                isMultipleDropDown={true}
                isLimitOptions={true}
                optional={true}
                options={recalProcessAndGenState.dynamicOptions.customerCodeDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    recalProcessAndGenVM.onGenerateFieldChange(fieldKey, fieldValue && fieldValue, fFullValue)} />
        </div>
        , [generateCriteria?.customerCodeList, SCREEN_CONSTANT.CUSTOMER_CODE, recalProcessAndGenState.dynamicOptions.customerCodeDropdownOptions, recalProcessAndGenVM])

    const memoRevenueAccountCode = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={generateCriteria.consolidationMethod !== "SEQ"}
                isShowOptional={false}
                readOnlyValue={generateCriteria?.revenueAccountCode ?? ''}
                fieldValue={generateCriteria?.revenueAccountCode}
                fieldLabel={SCREEN_CONSTANT.REVENUE_ACCOUNT_CODE}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'revenueAccountCode'}
                maxLength={25}
                isTextFieldCapitalize={true}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    recalProcessAndGenVM.onGenerateFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [generateCriteria.consolidationMethod, generateCriteria?.revenueAccountCode, SCREEN_CONSTANT.REVENUE_ACCOUNT_CODE, recalProcessAndGenVM])

    const memoRevenueCompanyCode = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={true}
                isShowOptional={false}
                readOnlyValue={generateCriteria.consolidationMethod === "SEQ" ? companyCode : ''}
                fieldValue={generateCriteria.consolidationMethod === "SEQ" ? companyCode : ''}
                fieldLabel={SCREEN_CONSTANT.REVENUE_COMPANY_CODE}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'revenueCompanyCode'}
                maxLength={60}
                isTextFieldCapitalize={true}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    recalProcessAndGenVM.onGenerateFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [generateCriteria.consolidationMethod, companyCode, SCREEN_CONSTANT.REVENUE_COMPANY_CODE, recalProcessAndGenVM])

    const memoCostCentreCode = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={generateCriteria.consolidationMethod !== "SEQ"}
                isShowOptional={false}
                readOnlyValue={generateCriteria?.costCentreCode ?? ''}
                fieldValue={generateCriteria?.costCentreCode}
                fieldLabel={SCREEN_CONSTANT.COST_CENTER_CODE}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'costCentreCode'}
                maxLength={25}
                isTextFieldCapitalize={true}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    recalProcessAndGenVM.onGenerateFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [generateCriteria.consolidationMethod, generateCriteria?.costCentreCode, SCREEN_CONSTANT.COST_CENTER_CODE, recalProcessAndGenVM])

    const memoIntercompanyCode = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={generateCriteria.consolidationMethod !== "SEQ"}
                isShowOptional={false}
                readOnlyValue={generateCriteria?.intercompanyCode ?? ''}
                fieldValue={generateCriteria?.intercompanyCode}
                fieldLabel={SCREEN_CONSTANT.INTERCOMPANY_CODE}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'intercompanyCode'}
                maxLength={25}
                isTextFieldCapitalize={true}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    recalProcessAndGenVM.onGenerateFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [generateCriteria.consolidationMethod, generateCriteria?.intercompanyCode, SCREEN_CONSTANT.INTERCOMPANY_CODE, recalProcessAndGenVM])

    const memoProductServiceCode = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={generateCriteria.consolidationMethod !== "SEQ"}
                isShowOptional={false}
                readOnlyValue={generateCriteria?.productServiceCode ?? ''}
                fieldValue={generateCriteria?.productServiceCode}
                fieldLabel={SCREEN_CONSTANT.PRODUCT_OR_SERVICE_CODE}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'productServiceCode'}
                maxLength={25}
                isTextFieldCapitalize={true}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    recalProcessAndGenVM.onGenerateFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [generateCriteria.consolidationMethod, generateCriteria?.productServiceCode, SCREEN_CONSTANT.PRODUCT_OR_SERVICE_CODE, recalProcessAndGenVM])

    const memoProjectCode = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={generateCriteria.consolidationMethod !== "SEQ"}
                isShowOptional={false}
                readOnlyValue={generateCriteria?.projectCode ?? ''}
                fieldValue={generateCriteria?.projectCode}
                fieldLabel={SCREEN_CONSTANT.PROJECT_CODE}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'projectCode'}
                maxLength={25}
                isTextFieldCapitalize={true}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    recalProcessAndGenVM.onGenerateFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [generateCriteria.consolidationMethod, generateCriteria?.projectCode, SCREEN_CONSTANT.PROJECT_CODE, recalProcessAndGenVM])

    const memoSalesChannelCode = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={generateCriteria.consolidationMethod !== "SEQ"}
                isShowOptional={false}
                readOnlyValue={generateCriteria?.salesChannelCode ?? ''}
                fieldValue={generateCriteria?.salesChannelCode}
                fieldLabel={SCREEN_CONSTANT.SALES_CHANNEL_CODE}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'salesChannelCode'}
                maxLength={25}
                isTextFieldCapitalize={true}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    recalProcessAndGenVM.onGenerateFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [generateCriteria.consolidationMethod, generateCriteria?.salesChannelCode, SCREEN_CONSTANT.SALES_CHANNEL_CODE, recalProcessAndGenVM])

    const memoCountryCode = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={true}
                isShowOptional={false}
                readOnlyValue={generateCriteria.consolidationMethod === "SEQ" ? countryCode : ''}
                fieldValue={generateCriteria.consolidationMethod === "SEQ" ? countryCode : ''}
                fieldLabel={SCREEN_CONSTANT.COUNTRY_CODE}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'countryCode'}
                maxLength={60}
                isTextFieldCapitalize={true}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    recalProcessAndGenVM.onGenerateFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [generateCriteria.consolidationMethod, countryCode, SCREEN_CONSTANT.COUNTRY_CODE, recalProcessAndGenVM])

    const memoSpareCode = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={generateCriteria.consolidationMethod !== "SEQ"}
                isShowOptional={false}
                readOnlyValue={generateCriteria?.spareCode ?? ''}
                fieldValue={generateCriteria?.spareCode}
                fieldLabel={SCREEN_CONSTANT.SPARE_CODE}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'spareCode'}
                maxLength={25}
                isTextFieldCapitalize={true}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    recalProcessAndGenVM.onGenerateFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [generateCriteria.consolidationMethod, generateCriteria?.spareCode, SCREEN_CONSTANT.SPARE_CODE, recalProcessAndGenVM])

    const memoDesc1 = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "470px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={generateCriteria.consolidationMethod !== "SEQ"}
                isShowOptional={true}
                readOnlyValue={generateCriteria?.desc1 ?? ''}
                fieldValue={generateCriteria?.desc1}
                fieldLabel={SCREEN_CONSTANT.DESC1}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'desc1'}
                maxLength={150}
                isTextFieldCapitalize={true}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    recalProcessAndGenVM.onGenerateFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [generateCriteria.consolidationMethod, generateCriteria?.desc1, SCREEN_CONSTANT.DESC1, recalProcessAndGenVM])

    const memoDesc2 = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "470px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={generateCriteria.consolidationMethod !== "SEQ"}
                isShowOptional={true}
                readOnlyValue={generateCriteria?.desc2 ?? ''}
                fieldValue={generateCriteria?.desc2}
                fieldLabel={SCREEN_CONSTANT.DESC2}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'desc2'}
                maxLength={150}
                isTextFieldCapitalize={true}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    recalProcessAndGenVM.onGenerateFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [generateCriteria.consolidationMethod, generateCriteria?.desc2, SCREEN_CONSTANT.DESC2, recalProcessAndGenVM])

    const memoDocDateFromTo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "470px", marginBottom: "24px" }}>
            <DateRangePickerComponent
                label={SCREEN_CONSTANT.DOC_DATE_RANGE}
                width='470px'
                fieldNames={{ startField: "docDateFrom", endField: "docDateTo" }}
                optional={true}
                dateRange={{ startDate: generateCriteria.docDateFrom, endDate: generateCriteria.docDateTo }}
                onDatesChange={recalProcessAndGenVM.onGenDateRangeChange}
            />
        </div>
        , [SCREEN_CONSTANT.DOC_DATE_RANGE, recalProcessAndGenVM.onGenDateRangeChange, generateCriteria.docDateFrom, generateCriteria.docDateTo])

    return (
        <div className='side-form-content-wrapper'>
            <div className={'flex-row'}>
                <div className={'flex-row-item flex-row-item-full-width'}>
                    <Sidebarheader>
                        <SidebarTitle>
                            {"Recalculation Generate"}
                        </SidebarTitle>
                        <SidebarActionCross>
                            <IconButton
                                fileName="Icon-cross" size="medium" toolTipText='Close' toolTipArrow={false} onClick={handClose} />
                        </SidebarActionCross>
                    </Sidebarheader>
                </div>
            </div>
            {isLoading &&
                <Loader Indicator="Spinner" size="Medium" />}
            {!isLoading && <>
                <div className={'add-edit-form'} style={{ height: '75vh', maxHeight: '75vh', overflow: 'auto' }}>
                    <div className="flex-row-auto">
                        <div className={'flex-row-item'}>

                            <CriteriaItemContainer>
                                {memoSeqNoFrom}
                                {memoSeqNoTo}
                            </CriteriaItemContainer>

                            {(operatingUnit === "AQCT" || operatingUnit === "OICT" || operatingUnit === "HPJZ" || operatingUnit === "RSCT") &&
                                <CriteriaItemContainer>
                                    {memoDocDateFromTo}
                                </CriteriaItemContainer>
                            }
                            <CriteriaItemContainer>
                                {memoBillToCompany}
                                {memoCustomerCode}
                            </CriteriaItemContainer>

                            <CriteriaItemContainer>
                                {memoConsolidationMethodGroup}
                            </CriteriaItemContainer>

                            <CriteriaItemContainer>
                                {memoRevenueAccountCode}
                                {memoRevenueCompanyCode}
                            </CriteriaItemContainer>

                            <CriteriaItemContainer>
                                {memoCostCentreCode}
                                {memoIntercompanyCode}
                            </CriteriaItemContainer>

                            <CriteriaItemContainer>
                                {memoProductServiceCode}
                                {memoProjectCode}
                            </CriteriaItemContainer>

                            <CriteriaItemContainer>
                                {memoSalesChannelCode}
                                {memoCountryCode}
                            </CriteriaItemContainer>

                            <CriteriaItemContainer>
                                {memoSpareCode}
                            </CriteriaItemContainer>

                            <CriteriaItemContainer>
                                {memoDesc1}
                            </CriteriaItemContainer>

                            <CriteriaItemContainer>
                                {memoDesc2}
                            </CriteriaItemContainer>

                        </div>
                    </div>
                </div>
                <SidebarActionBar>
                    {/* <HPHButton label={'Close'} size={'Small'} theme={'Secondary'} onClick={handClose} /> */}
                    <HPHButton label={'Reset'} size={'Small'} theme={'Secondary'} onClick={resetButtonClicked} />
                    <HPHButton label={'Generate'} size={'Small'} theme={'Primary'} onClick={handleGenerate} />
                </SidebarActionBar>
            </>}
        </div>
    );
}

export default RecalProcessAndGenGeneratePanel;
